<template>
  <div>
    <v-menu
      v-model="show"
      light
      absolute
      :position-x="x"
      :position-y="y"
      offset-y
      max-width="95%"
    >
      <v-list v-if="free">
        <h-numeric-menu
          v-if="askGuests"
          :title="__('guests')"
          @input="book"
        >
          <template #activator="{ attrs, on }">
            <v-list-item
              v-bind="attrs"
              v-on="on"
            >
              <v-list-item-icon>
                <v-icon v-text="icons.table" />
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title v-text="__('newPending')" />
              </v-list-item-content>
            </v-list-item>
          </template>
        </h-numeric-menu>
        <v-list-item
          v-else
          @click="book(1)"
        >
          <v-list-item-icon>
            <v-icon v-text="icons.table" />
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title v-text="__('newPending')" />
          </v-list-item-content>
        </v-list-item>
      </v-list>
      <v-list v-else>
        <v-list-item
          v-if="can('forceUnlock')"
          @click="forceUnlock()"
        >
          <v-list-item-icon>
            <v-icon v-text="icons.unlock" />
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title v-text="__('forceUnlock')" />
          </v-list-item-content>
        </v-list-item>
        <v-list-item
          v-if="can('cancelOrder')"
          :disabled="cancelOrderDisabled"
          @click="cancelOrderDialog = true"
        >
          <v-list-item-icon>
            <v-icon v-text="icons.cancel" :color="cancelOrderDisabled ? 'grey' : 'primary'" />
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title v-text="__('cancelOrder')" />
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-menu>
    <h-reason
      v-model="cancelOrderDialog"
      :title="__('cancelOrder')"
      :label="__('cancelOrderReason')"
      @submit="tryToCancelOrder"
    />
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import I18n from '@/Mixins/I18n'
  import InteractsWithLoading from '@/Mixins/InteractsWithLoading'
  import NeedsPermission from '@/Mixins/NeedsPermission'
  import Userable from '@/Mixins/Userable'
  import UsesIcons from '@/Mixins/UsesIcons'
  import FranceLog from '@/Models/Static/FiscalCountry/FranceLog'
  import HNumericMenu from '@/Components/NumericMenu'
  import HReason from '@/Components/Reason'

  export default {
    mixins: [
      I18n,
      InteractsWithLoading,
      NeedsPermission,
      Userable,
      UsesIcons,
    ],

    components: {
      HNumericMenu,
      HReason,
    },

    props: {
      value: {
        required: true,
        type: Boolean,
      },
      x: {
        required: true,
        type: Number,
      },
      y: {
        required: true,
        type: Number,
      },
      free: {
        required: true,
        type: Boolean,
      },
      // May be the Order's ID or the Table's ID
      id: {
        required: true,
        type: Number,
      },
    },

    data: () => ({
      FranceLog,
      cancelOrderDialog: false,
    }),

    computed: {
      UUID () {
        return this.$store.state.UUID
      },
      show: {
        get () {
          return this.value
        },
        set (v) {
          this.$emit('input', v)
        }
      },
      cancelOrderDisabled () {
        return Boolean(this.$store.getters['orders/get'](this.id)?.orderInvoices.length)
      },
      askGuests () {
        return this.$store.getters['orders/config/shouldAskGuestsForTable']
      },
      ...mapGetters('rummage', [
        'cashier',
      ]),
    },

    methods: {
      book (guests) {
        this.show = false
        this.$emit('book', {
          table_id: this.id,
          guests,
        })
      },
      forceUnlock () {
        if (this.loading) {
          return
        }
        this.startLoading()
        this.$log('Force unlocking...', 'Order', this.id)
        this.$api('forceUnlockOrder', {
          orderId: this.id,
          uuid: this.UUID,
        })
          .then(success => {
            this.$log(`Force unlock ${success ? 'success' : 'failed'}`, 'Order', this.id)
            const trans = success ? 'forceUnlockSuccess' : 'forceUnlockFailed'
            const alert = success ? 'alert/showSuccess' : 'alert/showWarning'
            this.$store.dispatch(alert, this.__(trans))
          })
          .catch(e => this.$store.dispatch('xError', e))
          .finally(() => this.stopLoading())
      },
      tryToCancelOrder (reason) {
        this.startLoading()
        this.$log('Cancelling...', 'Order', this.id)
        this.$api('lockOrder', {
          orderId: this.id,
          uuid: this.UUID,
        })
          .then(r => {
            if (r.data?.success !== true) {
              this.$log(`Locked by ${r.data.deviceName}`, 'Order', this.id)
              this.$store.dispatch('alert/showError', this.__('lockedBy') + `: ${r.data.deviceName}`)
              return
            }
            this.cancelOrder(reason)
          })
          .catch(e => this.$store.dispatch('xError', e))
          .finally(() => this.stopLoading())
      },
      cancelOrder (reason) {
        this.$api('cancelOrder', {
          reason,
          orderId: this.id,
          warehouse_id: this.cashier.warehouse_id,
          tenantUserId: this.user.id,
        })
          .then(r => {
            this.$log('Cancelled', 'Order', this.id)
            this.$logFrance(this.FranceLog.ABANDONMENT_OF_A_NON_VALIDATED_SALE_TRANSACTION, `Cancelled order ${this.id}: ${reason}`)
            this.$store.commit('orders/remove', this.id)
          })
          .catch(e => this.$store.dispatch('xError', e))
          .finally(() => this.stopLoading())
      },
    },
  }
</script>
